import { MyNaviCollegeDetail } from '@/components/Campaign/mynavi/MyNaviModels/College/MyNaviCollegeDetail'
import { useApi } from '@/core/api/useApi'
import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import type { MynaviCollegeDetailResponse } from '@/schemas/v2/mynavi'
import { useTokenStore } from '@/store/useTokenStore'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'

type CollegeDetailProps = {
  /** 表示する学校 ID（XState context から渡される） */
  collegeId: number
  /** 前の画面に戻るコールバック（XState NAVIGATE_BACK を発火する） */
  onBack: () => void
}

export const CollegeDetail = ({ collegeId, onBack }: CollegeDetailProps) => {
  const router = useRouter()
  const api = useApi()
  const { loginUrl } = useCampaignSlug()
  const idToken = useTokenStore((s) => s.idToken)

  const [college, setCollege] = useState<MynaviCollegeDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isWishedMap, setIsWishedMap] = useState<Record<number, boolean>>({})

  // biome-ignore lint/correctness/useExhaustiveDependencies: 学校 ID とログイン状態が変わったときだけ再取得する（api / onBack は毎レンダーで参照が変わり得る）
  useEffect(() => {
    const fetchDetail = idToken ? api.auth.fetchMynaviCollegeDetail : api.guest.fetchMynaviCollegeDetail
    fetchDetail(collegeId, true)
      .then((data) => {
        setCollege(data)
        const map: Record<number, boolean> = {}
        for (const oc of data.openCampuses) {
          map[oc.id] = oc.isWished
        }
        setIsWishedMap(map)
      })
      .catch(() => {
        onBack()
      })
      .finally(() => setIsLoading(false))
  }, [collegeId, idToken])

  const handleToggleWanted = useCallback(
    async (ocId: number) => {
      // 未ログイン時はログインページへリダイレクト（認証必須エンドポイントへの 401 防止）
      if (!idToken) {
        router.push(loginUrl)
        return
      }
      const next = !(isWishedMap[ocId] ?? false)
      setIsWishedMap((prev) => ({ ...prev, [ocId]: next }))
      try {
        await api.auth.postMynaviWishToVisit(ocId, next)
      } catch {
        setIsWishedMap((prev) => ({ ...prev, [ocId]: !next }))
      }
    },
    [api, idToken, isWishedMap, loginUrl, router],
  )

  if (isLoading || !college) return null

  return (
    <MyNaviCollegeDetail
      college={college}
      isWishedMap={isWishedMap}
      onBack={onBack}
      onBrochureRequest={() => {
        if (college.brochureUrl) window.open(college.brochureUrl, '_blank', 'noopener,noreferrer')
      }}
      onEventInfo={() => {
        if (college.openCampusUrl) window.open(college.openCampusUrl, '_blank', 'noopener,noreferrer')
      }}
      onToggleWanted={handleToggleWanted}
    />
  )
}
